import { useCallback, useEffect, useRef, useState } from 'react';
import type { QuestionMarker } from '../../shared/types';

/**
 * Phone-friendly audio recorder for the shoot view.
 *
 * Wraps the browser MediaRecorder API and adds:
 *   - mime negotiation (Opus on Android Chrome, AAC in MP4 on iOS Safari)
 *   - mono 32 kbps capture so a 30-min take stays under the Workers Free
 *     request body limit (~9 MB by then, well clear of 10 MB)
 *   - a hard auto-stop at 29:55 with 25-min and 29-min countdown warnings
 *     surfaced through hook state, so the UI can show banners
 *   - screen wake lock while recording (best-effort; iOS support landed
 *     in 16.4)
 *   - per-question markers driven by a `markQuestion(index)` call
 *
 * The hook deliberately does *not* upload — `stop()` resolves with the
 * resulting Blob and metadata, and the caller decides what to do with it.
 * Keeps networking concerns out of the recording state machine.
 */

export type RecorderState =
  | 'idle'
  | 'requesting' // permissions / device init
  | 'recording'
  | 'stopping'
  | 'error';

/** Soft warning thresholds. The hook only exposes them as flags; the UI
 *  decides how loud to be. */
const WARN_AT_MS = 25 * 60 * 1000; // 25 min — gentle "5 min left" hint
const URGENT_AT_MS = 29 * 60 * 1000; // 29 min — "60s remaining" alert
const AUTO_STOP_AT_MS = 29 * 60 * 1000 + 55 * 1000; // 29:55 — hard cap

// Number of frequency-band buckets reported via `audioLevels`. Five bars
// hits the sweet spot between "that's clearly a moving meter" and the
// hook doing more DSP work than necessary on a phone.
const LEVEL_BAND_COUNT = 5;

/** Picks the best mimeType the current browser supports. iOS Safari
 *  returns the empty string from MediaRecorder.isTypeSupported for some
 *  webm variants but quietly accepts audio/mp4 via constructor — so we
 *  probe in a sensible order and let the browser pick a default if none
 *  succeed. */
function pickMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/aac',
  ];
  if (typeof MediaRecorder === 'undefined') return '';
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      // older browsers throw rather than return false
    }
  }
  return '';
}

export interface RecorderResult {
  blob: Blob;
  mimeType: string;
  durationMs: number;
  markers: QuestionMarker[];
}

export interface RecorderStartResult {
  ok: boolean;
  error: string | null;
}

export interface UseRecorderReturn {
  state: RecorderState;
  /** Milliseconds elapsed since the most recent start(). 0 when idle. */
  elapsedMs: number;
  /** True once the take crosses the 25-minute mark. */
  warnSoftly: boolean;
  /** True once the take crosses the 29-minute mark — hard stop imminent. */
  warnUrgently: boolean;
  /** Last error surfaced to the UI; cleared on next start(). */
  error: string | null;
  /** Dismisses the last recording error. The shoot page shares one banner
   *  across recording and PDF errors, so a stale mic message would otherwise
   *  outrank — and hide — whatever failed next. */
  clearError: () => void;
  /** Begin a fresh take. The resolved result is safe to inspect immediately;
   *  React state updates may not have committed yet. */
  start: () => Promise<RecorderStartResult>;
  /** End the current take. Resolves with the recorded blob + metadata, or
   *  null if there was nothing to record (start failed, etc). */
  stop: () => Promise<RecorderResult | null>;
  /** Append a marker for the currently-active question. Safe to call when
   *  not recording — it's a no-op. */
  markQuestion: (questionIndex: number) => void;
  /** Live read of the markers collected so far during the take. */
  markers: QuestionMarker[];
  /**
   * Per-band normalised audio amplitude (0..1) sampled live from the mic.
   * Length is fixed at 5 bands. All zeros while idle; updates roughly 30
   * times per second while recording. The waveform component reads this
   * directly; it's the on-screen "yes, I'm picking up sound" signal.
   */
  audioLevels: number[];
}

export function useRecorder(): UseRecorderReturn {
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [markers, setMarkers] = useState<QuestionMarker[]>([]);
  const [audioLevels, setAudioLevels] = useState<number[]>(() =>
    new Array(LEVEL_BAND_COUNT).fill(0),
  );

  // Mutable refs for things that don't drive renders. MediaRecorder, the
  // captured chunks, the wake lock sentinel, and the start timestamp all
  // belong here so we don't churn React state on every dataavailable.
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);
  // The wake-lock API isn't in lib.dom.d.ts on older TS configs; declare
  // loosely. Sentinel becomes null when the page is backgrounded; we
  // re-acquire on visibility return.
  const wakeLockRef = useRef<{ release: () => Promise<void> } | null>(null);
  // Captured at start so stop() returns a stable mime even if the
  // MediaRecorder mid-stream invents a slightly different type string.
  const mimeTypeRef = useRef<string>('');
  // Resolver for the in-flight stop() call so we can hand the blob back
  // once MediaRecorder.onstop fires asynchronously after we ask it to stop.
  const stopResolveRef = useRef<((r: RecorderResult | null) => void) | null>(
    null,
  );

  // Web Audio analyser graph attached to the same MediaStream the
  // MediaRecorder is consuming. Lives next to the recorder so it gets
  // torn down at the same time.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const freqBufferRef = useRef<Uint8Array | null>(null);
  // Throttle React state updates to ~30 fps so the bars feel alive
  // without thrashing the renderer at 60+ fps for a 5-element array.
  const lastLevelUpdateRef = useRef<number>(0);
  // Mirror the latest level array in a ref so onstop can read it without
  // racing against the React state update.
  const audioLevelsRef = useRef<number[]>(new Array(LEVEL_BAND_COUNT).fill(0));

  // ---- Wake lock helpers ----

  const acquireWakeLock = useCallback(async () => {
    try {
      // navigator.wakeLock landed in iOS 16.4. Fall through silently on
      // unsupported browsers — the screen will lock per OS settings.
      const nav = navigator as unknown as {
        wakeLock?: { request: (kind: string) => Promise<{ release: () => Promise<void> }> };
      };
      if (!nav.wakeLock) return;
      wakeLockRef.current = await nav.wakeLock.request('screen');
    } catch {
      // Not fatal; recording continues without the lock.
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    const lock = wakeLockRef.current;
    wakeLockRef.current = null;
    if (lock) {
      try {
        await lock.release();
      } catch {
        // ignore
      }
    }
  }, []);

  // Re-request the lock if the user backgrounds and returns to the tab
  // mid-recording — Safari aggressively releases it on visibilitychange.
  useEffect(() => {
    const onVis = () => {
      if (
        document.visibilityState === 'visible' &&
        recorderRef.current?.state === 'recording' &&
        !wakeLockRef.current
      ) {
        void acquireWakeLock();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [acquireWakeLock]);

  // ---- Cleanup on unmount: don't leak the mic ----

  useEffect(() => {
    return () => {
      try {
        recorderRef.current?.stop();
      } catch {
        // ignore
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (tickRef.current !== null) cancelAnimationFrame(tickRef.current);
      void releaseWakeLock();
    };
  }, [releaseWakeLock]);

  const clearError = useCallback(() => setError(null), []);

  // ---- Start ----

  const start = useCallback(async () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      return { ok: false, error: 'A recording is already in progress.' };
    }
    setError(null);
    setMarkers([]);
    setElapsedMs(0);
    chunksRef.current = [];
    setState('requesting');

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        // Mono speech with browser-managed echo cancellation + noise
        // suppression — the right defaults for an interview held on a
        // phone in a moderately noisy room.
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Microphone access was denied or unavailable.';
      setState('error');
      setError(message);
      return { ok: false, error: message };
    }
    streamRef.current = stream;

    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
        // 32 kbps mono Opus / AAC. ~7 MB for 30 min — fits Workers Free
        // request body limit with comfortable headroom. Voice-quality, not
        // music-quality, which is exactly what we want for transcription.
        audioBitsPerSecond: 32_000,
      });
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      const message = err instanceof Error ? err.message : 'Could not start the recorder.';
      setState('error');
      setError(message);
      return { ok: false, error: message };
    }

    mimeTypeRef.current = recorder.mimeType || mimeType || 'audio/webm';
    recorderRef.current = recorder;

    // Spin up a Web Audio graph so we can read live amplitude alongside
    // recording. This is *separate* from MediaRecorder's audio path —
    // both consume the same MediaStream concurrently, MediaRecorder
    // writes Opus/AAC to disk while the AnalyserNode just reports
    // current levels for the on-screen waveform.
    try {
      const Ctx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        // Some browsers (notably iOS Safari) start AudioContexts in a
        // suspended state; resume after a user gesture (which start() is).
        if (ctx.state === 'suspended') {
          void ctx.resume().catch(() => {
            // Not fatal — analyser will simply report zeros.
          });
        }
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        // Small FFT keeps the per-frame work cheap and gives us 32
        // frequency bins to bucket into our 5 visual bands.
        analyser.fftSize = 64;
        // Smoothing softens the bars between frames so they breathe
        // rather than flicker. 0.7 is the sweet spot for a "live but
        // not jittery" feel.
        analyser.smoothingTimeConstant = 0.7;
        source.connect(analyser);
        // Note: deliberately *not* connecting analyser → ctx.destination
        // — that would feed the mic back to the speakers and cause howl.
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
        sourceNodeRef.current = source;
        freqBufferRef.current = new Uint8Array(analyser.frequencyBinCount);
      }
    } catch {
      // Analyser is non-essential; recording proceeds without the
      // waveform if Web Audio fails to initialise.
    }

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
      const durationMs = Math.max(0, Date.now() - startedAtRef.current);
      const result: RecorderResult = {
        blob,
        mimeType: mimeTypeRef.current,
        durationMs,
        markers: markersRef.current.slice(),
      };
      // Surface the result to the awaiting stop() promise.
      stopResolveRef.current?.(result);
      stopResolveRef.current = null;

      // Tear down the mic stream so the OS recording indicator clears.
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      recorderRef.current = null;
      if (tickRef.current !== null) {
        cancelAnimationFrame(tickRef.current);
        tickRef.current = null;
      }
      // Tear down the analyser graph alongside the recorder. The
      // AudioContext is reusable but it's cheaper to spin a fresh one
      // for the next take than to manage lifecycle across pauses.
      try {
        sourceNodeRef.current?.disconnect();
      } catch {
        // ignore
      }
      sourceNodeRef.current = null;
      analyserRef.current = null;
      freqBufferRef.current = null;
      if (audioCtxRef.current) {
        void audioCtxRef.current.close().catch(() => {
          // ignore
        });
        audioCtxRef.current = null;
      }
      const zeros = new Array(LEVEL_BAND_COUNT).fill(0);
      audioLevelsRef.current = zeros;
      setAudioLevels(zeros);
      void releaseWakeLock();
      setState('idle');
    };

    // Request periodic chunks — without a timeslice some MediaRecorder
    // implementations only fire dataavailable on stop(). 1 s gives us a
    // smooth recovery path if we add IndexedDB persistence later.
    recorder.start(1000);
    startedAtRef.current = Date.now();
    void acquireWakeLock();
    setState('recording');

    // rAF-driven elapsed clock keeps the elapsed counter and the
    // auto-stop timer in lockstep. setInterval drifts on backgrounded
    // tabs in iOS Safari — animation frames pause cleanly instead, which
    // is the desired behaviour: an off-screen tab shouldn't be tallying
    // wall-clock time it's not actually capturing audio for.
    //
    // The same loop also samples the AnalyserNode and pushes new
    // audioLevels through React state at ~30 fps — fast enough to feel
    // alive, slow enough not to thrash the renderer.
    const tick = () => {
      const ms = Date.now() - startedAtRef.current;
      setElapsedMs(ms);

      const analyser = analyserRef.current;
      const buf = freqBufferRef.current;
      const now = performance.now();
      if (analyser && buf && now - lastLevelUpdateRef.current >= 33) {
        lastLevelUpdateRef.current = now;
        analyser.getByteFrequencyData(buf as unknown as Uint8Array<ArrayBuffer>);
        // Bucket the FFT bins into LEVEL_BAND_COUNT visual bands.
        // Each band gets the average byte level (0..255) over its
        // frequency range, then we normalise to 0..1.
        const bins = buf.length;
        const perBand = Math.max(1, Math.floor(bins / LEVEL_BAND_COUNT));
        const levels = new Array<number>(LEVEL_BAND_COUNT);
        for (let b = 0; b < LEVEL_BAND_COUNT; b++) {
          let sum = 0;
          const startBin = b * perBand;
          const endBin = b === LEVEL_BAND_COUNT - 1 ? bins : startBin + perBand;
          for (let i = startBin; i < endBin; i++) sum += buf[i];
          const avg = sum / Math.max(1, endBin - startBin);
          // Voice doesn't fill the dynamic range, so square-rooting the
          // 0..1 amplitude gives a more responsive bar — 30 dB of speech
          // moves a clearly-visible amount instead of staying near zero.
          const norm = Math.min(1, Math.sqrt(avg / 255));
          levels[b] = norm;
        }
        audioLevelsRef.current = levels;
        setAudioLevels(levels);
      }

      if (ms >= AUTO_STOP_AT_MS) {
        // Auto-stop. The onstop handler above will tear everything down.
        try {
          recorder.stop();
        } catch {
          // ignore
        }
        return;
      }
      tickRef.current = requestAnimationFrame(tick);
    };
    tickRef.current = requestAnimationFrame(tick);
    return { ok: true, error: null };
  }, [acquireWakeLock, releaseWakeLock]);

  // ---- Marker tracking ----
  //
  // We mirror markers into a ref so the recorder.onstop handler can read
  // the latest array synchronously — React state would be stale by then.

  const markersRef = useRef<QuestionMarker[]>([]);

  const markQuestion = useCallback(
    (questionIndex: number) => {
      if (recorderRef.current?.state !== 'recording') return;
      const t = Math.max(0, Date.now() - startedAtRef.current);
      const marker: QuestionMarker = { q: questionIndex, t };
      markersRef.current = [...markersRef.current, marker];
      setMarkers(markersRef.current);
    },
    [],
  );

  // Reset the ref alongside the state when starting a new take.
  useEffect(() => {
    markersRef.current = markers;
  }, [markers]);

  // ---- Stop ----

  const stop = useCallback((): Promise<RecorderResult | null> => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      return Promise.resolve(null);
    }
    setState('stopping');
    return new Promise<RecorderResult | null>((resolve) => {
      stopResolveRef.current = resolve;
      try {
        recorder.stop();
      } catch (err) {
        // If stop() throws (very rare), bail out gracefully.
        stopResolveRef.current = null;
        setError(err instanceof Error ? err.message : 'Failed to stop recorder.');
        resolve(null);
      }
    });
  }, []);

  return {
    state,
    elapsedMs,
    warnSoftly: elapsedMs >= WARN_AT_MS && elapsedMs < URGENT_AT_MS,
    warnUrgently: elapsedMs >= URGENT_AT_MS,
    error,
    clearError,
    start,
    stop,
    markQuestion,
    markers,
    audioLevels,
  };
}
